import { ZERO } from '@snap-types/types/decimal';

import {
  CalculateProductOptionPriceParams,
  CalculateProductOptionPriceResult,
} from './types';

/**
 * A lot-priced option charges per piece at the rate of the lot bracket the
 * quantity falls into, and the option also carries a minimum lot charge for
 * quantities under the first bracket.
 *
 * That minimum is deliberately absent from the price. Spreading it across the
 * quantity — the way a batch charge is spread — makes the finish contribute
 * exactly the minimum lot charge to the line total no matter how small the
 * quantity, which is at or above every RAF market minimum and so hides the
 * "quantity too low" warning the quote should be raising instead. Below the
 * first bracket the option prices at that bracket's rate, the line total falls
 * short, and the warning fires.
 */
export function calculateLotPriceAddition({
  option,
  quantity,
}: CalculateProductOptionPriceParams): CalculateProductOptionPriceResult {
  if (quantity.lte(ZERO)) {
    return {
      isSuccess: false,
      error: 'Quantity must be greater than 0',
      calculationMode: option.calculationMode,
    };
  }

  const sortedLotPrices = [...option.lotPrices].sort((a, b) =>
    a.qty.minus(b.qty).toNumber(),
  );

  const unmetThresholdIndex = sortedLotPrices.findIndex((lot) =>
    lot.qty.greaterThan(quantity),
  );

  let lotPrice = null;

  if (unmetThresholdIndex === 0) {
    // The quantity is below the first lot quantity, so it prices at the first
    // bracket's rate.
    lotPrice = sortedLotPrices[0];
  } else if (unmetThresholdIndex === -1) {
    // The quantity is greater than or equal to the last lot quantity, so we use
    // the last lot price
    lotPrice = sortedLotPrices[sortedLotPrices.length - 1];
  } else {
    lotPrice = sortedLotPrices[unmetThresholdIndex - 1];
  }

  if (!lotPrice) {
    return {
      isSuccess: false,
      error: 'Missing lot price',
      calculationMode: option.calculationMode,
    };
  }

  return {
    isSuccess: true,
    unitPriceAddition: lotPrice.unitPrice,
    calculationMode: option.calculationMode,
  };
}
